/**
 * Pre-built form field components.
 *
 * Each field wraps a shadcn/ui control and is registered in index.ts via
 * createFormHook, making them available as:
 *
 *   <form.AppField name="email">
 *     {(field) => <field.InputField label="Email" type="email" />}
 *   </form.AppField>
 *
 * All fields use FieldWrapper which handles layout, field masks
 * (hidden/disabled), and label/description/error rendering.
 *
 * To add a new field type: create the component here, then register it in
 * the fieldComponents object in index.ts. Port additional field types from
 * `packages/frontend/src/components/form/fields.tsx` as we need them.
 */

import { type ReactNode, useId } from 'react'

import { CardRadioGroup, type CardRadioOption } from '@/customer/components/card-radio-group'
import { useFieldContext, useFormContext } from '@/customer/components/form/form-context'
import {
  type FieldMasks,
  type FormLayout,
  getFieldMask,
  useFormLayout,
} from '@/customer/components/form/layouts/form-layout-context'
import { FormDescription, FormError, FormLabel } from '@/customer/components/form/primitives'
import { Input } from '@/customer/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/customer/components/ui/select'
import { Textarea } from '@/customer/components/ui/textarea'

import { getFormLayout } from './layouts'

// ─── Shared types & wrapper ─────────────────────────────────────────

export interface BaseFieldProps {
  label?: string
  /**
   * Whether the field is required. Defaults to `true`. Optional fields render
   * a muted `(optional)` marker next to the label.
   */
  required?: boolean
  description?: ReactNode
  layout?: FormLayout
  className?: string
  layoutClassName?: string
}

/** Narrowed version of TanStack Form's FieldApi — only the shape FieldWrapper needs. */
interface FieldCtx {
  name: string
  state: { meta: { errors: Array<string | { message: string } | undefined> } }
}

interface FormFieldProps extends Omit<BaseFieldProps, 'className'> {
  errors?: Array<string | { message: string } | undefined>
  /** Render the control element. */
  render: (ctx: { id: string; disabled: boolean }) => ReactNode
  /** Use `id` instead of `htmlFor` on the label (e.g. for checkbox/radio) */
  labelUsesId?: boolean
  /** Reverse the layout order (control before label, e.g. checkboxes) */
  reverseLayout?: boolean
  disabled?: boolean
}

/**
 * Field presentation with a linked label, control, description, and errors.
 * Layout and visual state are driven by props and the form layout context.
 */
export function FormField({
  errors = [],
  label,
  required,
  description,
  layoutClassName,
  layout,
  render,
  labelUsesId,
  reverseLayout,
  disabled = false,
}: FormFieldProps) {
  const id = useId()
  const resolvedLayout = useFormLayout(layout)
  const hasErrors = errors.length > 0

  const Layout = getFormLayout(resolvedLayout)

  return (
    <Layout
      className={layoutClassName}
      reverseLayout={reverseLayout}
      label={
        label && (
          <FormLabel
            {...(labelUsesId ? { id } : { htmlFor: id })}
            required={required}
            disabled={disabled}
            error={hasErrors}
          >
            {label}
          </FormLabel>
        )
      }
      description={description && <FormDescription>{description}</FormDescription>}
      control={render({ id, disabled })}
      error={<FormError errors={errors} />}
    />
  )
}

interface FieldWrapperProps extends Omit<FormFieldProps, 'errors'> {
  field: FieldCtx
  masks?: FieldMasks
}

/**
 * TanStack Form adapter for FormField, using field errors and visibility/disabled masks
 * to determine the field's presentation.
 */
export function FieldWrapper({ field, masks, disabled, ...props }: FieldWrapperProps) {
  const mask = getFieldMask(field.name, masks)
  if (mask === 'hidden') return null

  return (
    <FormField
      {...props}
      errors={field.state.meta.errors}
      disabled={disabled || mask === 'disabled'}
    />
  )
}

/**
 * Internal hook for registry fields — extracts field and masks from context
 * so they can be passed to FieldWrapper as explicit props.
 */
function useFieldWrapperContext() {
  // biome-ignore lint/suspicious/noExplicitAny: registry fields access value/handlers generically
  const field = useFieldContext<any>()
  const form = useFormContext()
  const masks = (form.options as { masks?: FieldMasks }).masks
  return { field, masks }
}

// ─── Input ───────────────────────────────────────────────────────────

interface InputFieldProps extends BaseFieldProps, React.ComponentProps<'input'> {}

export function InputField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: InputFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <Input
          id={id}
          type={props.type ?? 'text'}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── Textarea ───────────────────────────────────────────

interface TextareaFieldProps extends BaseFieldProps, React.ComponentProps<'textarea'> {}

export function TextareaField({
  label,
  required,
  description,
  className,
  layoutClassName,
  layout,
  ...props
}: TextareaFieldProps) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={props.disabled}
      render={({ id, disabled }) => (
        <Textarea
          id={id}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          disabled={disabled}
          aria-invalid={field.state.meta.errors.length > 0}
          className={className}
          {...props}
        />
      )}
    />
  )
}

// ─── Select ──────────────────────────────────────────────

export type SelectFieldOption<TValue extends string = string> = {
  value: TValue
  label: string
}

interface SelectFieldProps<TValue extends string = string> extends BaseFieldProps {
  options: readonly SelectFieldOption<TValue>[]
  placeholder?: string
  disabled?: boolean
}

export function SelectField<TValue extends string = string>({
  label,
  required,
  description,
  layoutClassName,
  layout,
  options,
  placeholder,
  disabled: disabledProp,
}: SelectFieldProps<TValue>) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      disabled={disabledProp}
      render={({ id, disabled }) => (
        <Select
          items={options as unknown as SelectFieldOption[]}
          value={(field.state.value as TValue) || null}
          onValueChange={(v) => field.handleChange(v as TValue)}
          disabled={disabled}
        >
          <SelectTrigger id={id}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  )
}

// ─── CardRadioGroup ──────────────────────────────────────

interface CardRadioGroupFieldProps<TValue extends string = string> extends BaseFieldProps {
  options: readonly CardRadioOption<TValue>[]
}

export function CardRadioGroupField<TValue extends string = string>({
  label,
  required,
  description,
  layoutClassName,
  layout,
  options,
}: CardRadioGroupFieldProps<TValue>) {
  const { field, masks } = useFieldWrapperContext()
  return (
    <FieldWrapper
      field={field}
      masks={masks}
      label={label}
      required={required}
      description={description}
      layoutClassName={layoutClassName}
      layout={layout}
      labelUsesId
      render={({ id }) => (
        <CardRadioGroup
          value={(field.state.value as TValue) || null}
          onValueChange={(v) => field.handleChange(v)}
          options={options}
          aria-labelledby={id}
        />
      )}
    />
  )
}
